import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    lat: Number, 
    long: Number, 
    apiKey: String,
    title: String,
    address: String,
    primaryColor: String,
    secondaryColor: String,
    surfaceColor: String,
    neutralColor: String,
    textColor: String,
    textMutedColor: String
  }

  connect() {
    if (!this.latValue || !this.longValue || isNaN(this.latValue) || isNaN(this.longValue)) {
      this.showError("Invalid coordinates provided")
      return
    }
    
    if (!this.apiKeyValue) {
      this.showError("Google Maps API key is missing")
      return
    }
    
    if (window.google && window.google.maps) {
      this.initializeMap()
    } else {
      this.loadGoogleMapsAPI()
    }
  }

  loadGoogleMapsAPI() {
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]')
    if (existingScript) {
      return
    }
    
    const script = document.createElement('script')
    const callbackName = `initGoogleMaps_${Date.now()}`
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKeyValue}&callback=${callbackName}`
    script.async = true
    script.defer = true
    
    script.onerror = () => {
      this.showError("Failed to load Google Maps")
    }
    
    window[callbackName] = () => {
      delete window[callbackName]
      this.initializeMap()
    }
    
    document.head.appendChild(script)
  }

  initializeMap() {
    try {
      const location = { lat: this.latValue, lng: this.longValue }
      
      if (!window.google || !window.google.maps) {
        this.showError("Google Maps API not loaded")
        return
      }
      
      const customStyles = [
        {
          "featureType": "all",
          "elementType": "geometry.fill",
          "stylers": [{ "color": this.surfaceColorValue }]
        },
        {
          "featureType": "road",
          "elementType": "geometry",
          "stylers": [{ "color": this.neutralColorValue }]
        },
        {
          "featureType": "road",
          "elementType": "geometry.stroke",
          "stylers": [{ "color": this.primaryColorValue }, { "weight": 0.5 }]
        },
        {
          "featureType": "road.highway",
          "elementType": "geometry",
          "stylers": [{ "color": this.secondaryColorValue }]
        },
        {
          "featureType": "road.arterial",
          "elementType": "geometry",
          "stylers": [{ "color": this.neutralColorValue }]
        },
        {
          "featureType": "landscape",
          "elementType": "geometry",
          "stylers": [{ "color": this.surfaceColorValue }]
        },
        {
          "featureType": "poi.park",
          "elementType": "geometry",
          "stylers": [{ "color": this.neutralColorValue }]
        },
        {
          "featureType": "poi.business",
          "elementType": "geometry",
          "stylers": [{ "color": this.surfaceColorValue }]
        },
        {
          "featureType": "water",
          "elementType": "geometry",
          "stylers": [{ "color": this.primaryColorValue }, { "lightness": 70 }]
        },
        {
          "featureType": "all",
          "elementType": "labels.text.fill",
          "stylers": [{ "color": this.textColorValue }]
        },
        {
          "featureType": "all",
          "elementType": "labels.text.stroke",
          "stylers": [{ "color": this.surfaceColorValue }, { "weight": 2 }]
        },
        {
          "featureType": "administrative",
          "elementType": "geometry.stroke",
          "stylers": [{ "color": this.primaryColorValue }, { "weight": 0.5 }]
        }
      ]
      
      this.element.innerHTML = ""
      
      const map = new google.maps.Map(this.element, {
        zoom: 15,
        center: location,
        styles: customStyles,
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        gestureHandling: 'cooperative'
      })
      
      const marker = new google.maps.Marker({
        position: location,
        map: map,
        title: this.titleValue || "Pet Resorts Australia",
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: this.primaryColorValue,
          fillOpacity: 1,
          strokeColor: this.secondaryColorValue,
          strokeWeight: 3,
          strokeOpacity: 1
        },
        animation: google.maps.Animation.DROP
      })

      const title = this.titleValue || 'Pet Resorts Australia'
      const address = this.addressValue || '11 Cicada Glen Drive, Ingleside NSW 2101'
      const searchQuery = encodeURIComponent(this.addressValue || 'Pet Resorts Australia Ingleside')
      
      const infoWindowContent = `<div style="padding: 12px; max-width: 250px;">` +
        `<h3 style="color: ${this.primaryColorValue}; margin: 0 0 8px 0; font-size: 16px; font-weight: 600;">${title}</h3>` +
        `<p style="margin: 0; color: ${this.textMutedColorValue}; font-size: 14px; line-height: 1.4;">${address}</p>` +
        `<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid ${this.neutralColorValue};">` +
        `<a href="https://maps.google.com/?q=${searchQuery}" target="_blank" style="color: ${this.primaryColorValue}; text-decoration: none; font-size: 13px; font-weight: 500;">Get Directions →</a>` +
        `</div></div>`
      
      const infoWindow = new google.maps.InfoWindow({
        content: infoWindowContent
      })
      
      setTimeout(() => {
        infoWindow.open(map, marker)
      }, 600)
      
      marker.addListener("click", () => {
        if (infoWindow.getMap()) {
          infoWindow.close()
        } else {
          infoWindow.open(map, marker)
        }
      })

      this.map = map
      this.marker = marker
      this.infoWindow = infoWindow
      
    } catch (error) {
      this.showError(`Map initialization failed: ${error.message}`)
    }
  }

  showError(message) {
    const errorHTML = `<div class="flex items-center justify-center h-full">` +
      `<div class="text-center p-4">` +
      `<div class="w-12 h-12 mx-auto mb-3" style="color: #ef4444;">` +
      `<i class="fas fa-exclamation-triangle text-2xl"></i>` +
      `</div>` +
      `<p class="text-sm font-medium" style="color: #ef4444; font-family: var(--font-body);">${message}</p>` +
      `<p class="text-xs mt-1" style="color: ${this.textMutedColorValue || '#666'}; font-family: var(--font-body);">Check console for details</p>` +
      `</div>` +
      `</div>`
    
    this.element.innerHTML = errorHTML
  }

  disconnect() {
    if (this.infoWindow) {
      this.infoWindow.close()
    }
    
    this.map = null
    this.marker = null
    this.infoWindow = null
  }
}
